// reportTemplate.ts

// Helper to escape strings for safe inclusion in template literals
export const escapeStringForTemplateLiteral = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value)
    .replace(/\\/g, '\\\\') // Must be first
    .replace(/`/g, '\\`')   // Escape backticks
    .replace(/\$\{/g, '\\${'); // Escape ${} - THIS ONE IS CORRECT (part of regex)
};

export const getProjectReportHTMLWithResolver = (
  projectNameForExport: string,
  projectDescription: string | undefined,
  reportCreationDate: string,
  totalDisplayedItemsInProjectSummary: number, // Renamed from itemsToExportLength, used for Project Summary card & footer
  totalSpentOnProject: number,
  teamsInvolved: string[],  // Changed from teamsInvolvedLength: number to teamsInvolved: string[]
  projectDeadlineFormatted: string,
  totalReceivedForProject: number,
  totalAllocatedToPurchasers: number,
  projectBudgetRemaining: number,
  displayedPurchaserIds: string[], // Renamed from allRelevantPurchaserIds
  getPrecalculatedPurchaserData: (purchaserId: string) => { name: string; team: string; spent: number; allocated: number; items: any[] } | undefined, // Function to get precalculated data
  t: (key: string, options?: any) => string,
  fundAttachmentsHtml?: string,
  totalAdditionalFundsGiven?: number, // Separate additional funds given
  totalFundsReturned?: number, // Separate funds returned
  separateAttachmentsHtml?: string // Separate attachments section
): string => {
  // Pre-escape all values that will be inserted into the HTML
  const escapedProjectName = escapeStringForTemplateLiteral(projectNameForExport);
  const escapedDescription = escapeStringForTemplateLiteral(projectDescription) || t('project.description.none', { defaultValue: 'ไม่มีคำอธิบายโครงการ' });
  const escapedDeadline = escapeStringForTemplateLiteral(projectDeadlineFormatted);

  // Build purchaser sections
  const purchaserSections = displayedPurchaserIds.length > 0 ? displayedPurchaserIds.map(purchaserId => {
    const pData = getPrecalculatedPurchaserData(purchaserId);
    if (!pData || (pData.items.length === 0 && pData.allocated === 0 && pData.spent === 0)) return '';

    const itemRows = pData.items.map(item => {
      const itemName = escapeStringForTemplateLiteral(item.name);
      const itemDetails = escapeStringForTemplateLiteral(item.details) || '';
      const itemUnit = escapeStringForTemplateLiteral(item.unit);
      const itemCategory = escapeStringForTemplateLiteral(item.category) || t('category.other', { defaultValue: 'ไม่ระบุ' });
      return `
      <tr>
        <td>
          <div class="item-name">
            ${itemName}
            ${item.isEmergency ? `<span class="emergency-badge">${t('item.emergency', { defaultValue: 'เร่งด่วน' })}</span>` : ''}
          </div>
          <div class="item-details">${itemDetails}</div>
          <div style="margin-top: 8px;">
            <span class="item-category">${itemCategory}</span>
          </div>
        </td>
        <td>${item.quantity} ${itemUnit}</td>
        <td class="price-cell">
          <div class="price-main">฿${(item.totalPrice || 0).toLocaleString()}</div>
          ${!item.isGroup ? `<div class="price-unit">฿${(item.price || 0).toLocaleString()}/${itemUnit}</div>` : `<div class="price-unit">${t('item.group.totalCost', { defaultValue: 'Total Group Cost' })}</div>`}
        </td>
        <td class="price-cell">
          <div class="price-main">฿${(item.shippingCost || 0).toLocaleString()}</div>
        </td>
        <td>
          <div class="attachments-count">${item.totalAttachmentsCount || 0} ${t('summary.filesUnit', { defaultValue: 'ไฟล์' })}</div>
        </td>
      </tr>
      `;
    }).join('');

    return `
    <div class="purchaser-section table-with-title">
      <div class="purchaser-header">
        <div class="purchaser-name">${pData.name} ${pData.team ? `(${pData.team})` : ''}</div>
        <div class="purchaser-stats">
          <div class="purchaser-stat">
            <div class="value">฿${pData.spent.toLocaleString()}</div>
            <div class="label">${t('fund.spent', { defaultValue: 'ใช้จ่าย' })}</div>
          </div>
          <div class="purchaser-stat">
            <div class="value">฿${pData.allocated.toLocaleString()}</div>
            <div class="label">${t('summary.totalFunds', { defaultValue: 'เงินทุน' })}</div>
          </div>
          <div class="purchaser-stat">
            <div class="value">${pData.items.length}</div>
            <div class="label">${t('general.items', { defaultValue: 'รายการ' })}</div>
          </div>
        </div>
      </div>
      ${pData.items.length > 0 ? `
      <table class="items-table">
        <thead>
          <tr>
            <th>${t('table.header.itemNameDetails', { defaultValue: 'รายการ' })}</th>
            <th>${t('table.header.quantityUnit', { defaultValue: 'จำนวน' })}</th>
            <th>${t('table.header.price', { defaultValue: 'ราคา' })}</th>
            <th>${t('table.header.shippingCost', { defaultValue: 'ค่าส่ง' })}</th>
            <th>${t('item.files', { defaultValue: 'ไฟล์แนบ' })}</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
        </tbody>
      </table>
      ` : `<p style="padding: 20px; text-align: center; color: #64748b;">${t('summary.noItemsForPurchaser', { defaultValue: 'ไม่มีรายการสำหรับผู้ซื้อนี้' })}</p>`}
    </div>
    `;
  }).join('') : `<p style="text-align: center; color: #64748b; padding: 20px;">${t('summary.noPurchaserData', { defaultValue: 'ไม่มีข้อมูลผู้ซื้อ' })}</p>`;

  return `<!DOCTYPE html>
<html lang="th">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>รายงานโครงการ - ${escapedProjectName}</title>
    <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Sarabun', Arial, sans-serif; line-height: 1.6; color: #1f2937; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; padding: 20px; }
        .container { max-width: 1200px; margin: 0 auto; background: white; border-radius: 20px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden; }
        .header { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; padding: 40px; text-align: center; position: relative; overflow: hidden; }
        .header::before { content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: repeating-linear-gradient( 45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 1px, transparent 1px, transparent 20px ); animation: float 20s linear infinite; }
        @keyframes float { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }
        .header-content { position: relative; z-index: 1; }
        .header h1 { font-size: 2.5rem; font-weight: 700; margin-bottom: 10px; text-shadow: 0 2px 4px rgba(0,0,0,0.2); }
        .header p { font-size: 1.1rem; opacity: 0.9; margin-bottom: 5px; }
        .header .meta { font-size: 0.95rem; opacity: 0.8; margin-top: 20px; }
        .content { padding: 40px; }
        .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 30px; margin-bottom: 40px; }
        .summary-card { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 16px; padding: 30px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 1px solid rgba(255, 255, 255, 0.5); transition: transform 0.3s ease; }
        .summary-card:hover { transform: translateY(-5px); }
        .summary-card h3 { font-size: 1.25rem; font-weight: 600; color: #1e40af; margin-bottom: 20px; display: flex; align-items: center; }
        .summary-card .icon { width: 24px; height: 24px; margin-right: 10px; background: #3b82f6; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; }
        .stat-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid rgba(148, 163, 184, 0.2); }
        .stat-item:last-child { border-bottom: none; }
        .stat-label { color: #64748b; font-weight: 500; }
        .stat-value { font-weight: 600; color: #1e293b; }
        .stat-value.positive { color: #059669; }
        .stat-value.negative { color: #dc2626; }
        .section-title { font-size: 1.875rem; font-weight: 700; color: #1e293b; margin: 40px 0 20px 0; padding-bottom: 10px; border-bottom: 3px solid #3b82f6; display: flex; align-items: center; page-break-inside: avoid; page-break-after: avoid; }
        .section-title .icon { width: 32px; height: 32px; background: #3b82f6; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; margin-right: 15px; font-size: 16px; }
        .purchaser-section { margin: 30px 0; background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 1px solid #e5e7eb; page-break-inside: avoid; }
        .purchaser-header { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); color: white; padding: 20px 30px; display: flex; justify-content: space-between; align-items: center; page-break-inside: avoid; page-break-after: avoid; }
        .purchaser-name { font-size: 1.25rem; font-weight: 600; }
        .purchaser-stats { display: flex; gap: 20px; font-size: 0.95rem; }
        .purchaser-stat { text-align: center; }
        .purchaser-stat .value { font-weight: 700; font-size: 1.1rem; }
        .purchaser-stat .label { opacity: 0.9; font-size: 0.85rem; }
        .items-table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
        .items-table th { background: #f8fafc; padding: 15px; text-align: left; font-weight: 600; color: #475569; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e2e8f0; page-break-inside: avoid; page-break-after: avoid; }
        .items-table td { padding: 15px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .items-table tr { page-break-inside: avoid; }
        .items-table tr:hover { background: rgba(59, 130, 246, 0.02); }
        .item-name { font-weight: 600; color: #1e293b; margin-bottom: 5px; }
        .item-details { color: #64748b; font-size: 0.9rem; line-height: 1.4; }
        .item-category { display: inline-block; background: #f1f5f9; color: #475569; padding: 4px 8px; border-radius: 6px; font-size: 0.8rem; font-weight: 500; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
        .status-approved { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); color: #065f46; border: 1px solid #10b981; }
        .emergency-badge { background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%); color: #991b1b; border: 1px solid #ef4444; font-size: 0.7rem; padding: 2px 6px; border-radius: 4px; margin-left: 8px; }
        .price-cell { text-align: right; }
        .price-main { font-weight: 700; font-size: 1.1rem; color: #1e293b; }
        .price-unit { color: #64748b; font-size: 0.85rem; }
        .attachments-count { background: #eff6ff; color: #1e40af; padding: 4px 8px; border-radius: 6px; font-size: 0.8rem; font-weight: 500; text-align: center; }
        /* Removed .footer styles */
        /* --- Attachment print/page-break styles --- */
        .attachment-report-block { margin: 2rem 0; text-align: center; }
        .attachment-report-title { font-size: 1.1rem; font-weight: 600; margin-bottom: 1rem; color: #1e293b; }
        .attachment-report-image { max-width: 90vw; max-height: 1000px; border: 1px solid #e5e7eb; border-radius: 8px; margin: 0 auto; display: block; background: #f8fafc; }
        .attachment-report-pdf { width: 100vw; height: 100vh; min-height: 1000px; border: none; background: #fff; display: block; margin: 0 auto; }
        .pdf-images-container { display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .pdf-page-image { max-width: 90vw; max-height: 800px; border: 1px solid #e5e7eb; border-radius: 8px; margin: 0.5rem auto; display: block; background: #f8fafc; page-break-inside: avoid; }
        .page-break { page-break-after: always; break-after: page; }
        .no-break { page-break-inside: avoid !important; break-inside: avoid !important; page-break-after: avoid !important; }
        .table-with-title { page-break-inside: avoid; }
        
        /* --- Smart Image Grid Styles (1 item per page with all images) --- */
        .item-attachments-page {
          page-break-inside: avoid;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .item-attachments-header {
          text-align: center;
          padding: 1rem;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          flex-shrink: 0;
        }
        .item-attachments-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }
        .item-attachments-subtitle {
          font-size: 0.95rem;
          color: #64748b;
        }
        .smart-image-grid {
          display: grid;
          gap: 0.75rem;
          width: 100%;
          flex: 1;
          align-content: start;
        }
        .smart-image-grid.single-image {
          grid-template-columns: 1fr;
          justify-items: center;
        }
        .smart-image-grid.single-image .grid-image-container {
          max-width: 90%;
        }
        .smart-image-grid.single-image .grid-image {
          max-height: 75vh;
        }
        .smart-image-grid.two-images {
          grid-template-columns: repeat(2, 1fr);
        }
        .smart-image-grid.two-images .grid-image {
          max-height: 60vh;
        }
        .smart-image-grid.few-images {
          grid-template-columns: repeat(2, 1fr);
        }
        .smart-image-grid.few-images .grid-image {
          max-height: 45vh;
        }
        .smart-image-grid.many-images {
          grid-template-columns: repeat(3, 1fr);
        }
        .smart-image-grid.many-images .grid-image {
          max-height: 35vh;
        }
        .grid-image-container {
          position: relative;
          background: #f8fafc;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .grid-image {
          width: 100%;
          height: auto;
          object-fit: contain;
          display: block;
        }
        .image-label {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 4px 8px;
          font-size: 0.75rem;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .other-files-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          flex-shrink: 0;
        }
        .other-file-item {
          background: white;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          font-size: 0.85rem;
        }
        .other-file-item a {
          color: #3b82f6;
          text-decoration: none;
        }
        .other-file-item a:hover {
          text-decoration: underline;
        }
        /* Aspect-ratio based styling (applied by JavaScript) */
        .grid-image-container.aspect-landscape {
          grid-column: span 2;
        }
        .grid-image-container.aspect-portrait {
          grid-row: span 2;
        }
        .grid-image-container.aspect-square {
          /* Default sizing */
        }
        /* Adjust for grids with mixed aspect ratios */
        .smart-image-grid.many-images .grid-image-container.aspect-landscape {
          grid-column: span 2;
        }
        .smart-image-grid.few-images .grid-image-container.aspect-landscape {
          grid-column: span 2;
        }
        @media print {
          html, body { 
            background: #fff !important; 
            margin: 0 !important;
            padding: 0 !important;
            font-size: 12pt !important;
            line-height: 1.4 !important;
          }
          @page { 
            size: A4 portrait; 
            margin: 12mm 8mm 12mm 8mm; 
          }
          .container { 
            max-width: 100% !important; 
            width: 100% !important; 
            margin: 0 !important; 
            border-radius: 0 !important; 
            box-shadow: none !important; 
            background: #fff !important; 
            border: none !important; 
            padding: 0 !important;
          }
          .summary-card, .purchaser-section, .header { 
            border-radius: 0 !important; 
            box-shadow: none !important; 
            background: #fff !important; 
            border: none !important; 
          }
          .header {
            padding: 8mm 0 6mm 0 !important;
            background: #fff !important;
            color: #000 !important;
          }
          .header h1 {
            font-size: 16pt !important;
            color: #000 !important;
            text-shadow: none !important;
            margin-bottom: 3mm !important;
          }
          .header p {
            font-size: 10pt !important;
            color: #000 !important;
            margin-bottom: 2mm !important;
          }
          .content { 
            padding: 0 5mm !important; 
          }
          .summary-grid { 
            gap: 3mm !important; 
            margin-bottom: 5mm !important; 
            grid-template-columns: repeat(auto-fit, minmax(70mm, 1fr)) !important;
          }
          .summary-card {
            padding: 5mm !important;
            border: 1px solid #ccc !important;
            font-size: 9pt !important;
          }
          .items-table { 
            font-size: 9pt !important; 
            border-collapse: collapse !important;
            width: 100% !important;
            border: 1px solid #ccc !important;
          }
          .items-table th, .items-table td { 
            padding: 3mm 2mm !important; 
            font-size: 9pt !important; 
            border: 1px solid #ddd !important; 
            text-align: left !important;
          }
          .items-table th {
            background: #f5f5f5 !important;
            font-weight: bold !important;
          }
          .items-table tr { 
            page-break-inside: avoid !important; 
          }
          .item-name { 
            font-size: 9pt !important; 
            font-weight: bold !important;
          }
          .item-details, .item-category, .price-unit, .attachments-count { 
            font-size: 8pt !important; 
          }
          .price-main {
            font-size: 9pt !important;
            font-weight: bold !important;
          }
          .section-title { 
            page-break-inside: avoid !important; 
            page-break-after: avoid !important; 
            margin-top: 8mm !important; 
            margin-bottom: 5mm !important;
            border-bottom: 2px solid #000 !important; 
            font-size: 14pt !important;
            color: #000 !important;
            padding-bottom: 2mm !important;
          }
          .purchaser-section { 
            page-break-inside: avoid !important; 
            margin: 5mm 0 !important; 
            border: 1px solid #ccc !important; 
          }
          .purchaser-header { 
            page-break-inside: avoid !important; 
            page-break-after: avoid !important; 
            background: #f0f0f0 !important;
            color: #000 !important;
            padding: 5mm !important;
            border-bottom: 1px solid #ccc !important;
          }
          .purchaser-name {
            font-size: 12pt !important;
            color: #000 !important;
          }
          .purchaser-stats {
            font-size: 9pt !important;
            color: #000 !important;
          }
          .table-with-title { 
            page-break-inside: avoid !important; 
          }
          .stat-item { 
            border-bottom: 1px solid #eee !important; 
          }
          .attachment-report-block { 
            margin: 5mm 0 !important; 
            padding: 0 !important; 
            page-break-inside: avoid !important;
            text-align: center !important;
          }
          .attachment-report-title { 
            font-size: 10pt !important; 
            margin-bottom: 3mm !important; 
            font-weight: bold !important;
            color: #000 !important;
          }
          .attachment-report-image { 
            max-width: 185mm !important; 
            max-height: 200mm !important; 
            width: auto !important;
            height: auto !important;
            border: 1px solid #ccc !important; 
            margin: 2mm auto !important;
            display: block !important;
          }
          .attachment-report-pdf { 
            width: 170mm !important; 
            height: 220mm !important; 
            border: 1px solid #ccc !important; 
            background: #fff !important; 
            margin: 2mm auto !important; 
            display: block !important; 
          }
          .pdf-images-container { 
            gap: 3mm !important; 
          }
          .pdf-page-image { 
            max-width: 170mm !important; 
            max-height: 220mm !important; 
            width: auto !important;
            height: auto !important;
            border: 1px solid #ccc !important; 
            margin: 2mm auto !important; 
            page-break-inside: avoid !important; 
            display: block !important;
          }
          .page-break { 
            page-break-after: always !important; 
            break-after: page !important; 
          }
          .container, .summary-card, .purchaser-section, .header { 
            box-shadow: none !important; 
          }
          /* Hide scrollbars in print for iframes */
          .attachment-report-pdf::-webkit-scrollbar { 
            display: none !important; 
          }
          .attachment-report-pdf { 
            scrollbar-width: none !important; 
            -ms-overflow-style: none !important; 
          }
          .no-break { 
            page-break-inside: avoid !important; 
            break-inside: avoid !important; 
            page-break-after: avoid !important; 
          }
          /* Smart Image Grid Print Styles */
          .item-attachments-page {
            page-break-inside: avoid !important;
            padding: 3mm !important;
            margin: 0 !important;
            display: block !important;
          }
          .item-attachments-header {
            padding: 3mm !important;
            background: #f5f5f5 !important;
            border: 1px solid #ccc !important;
            margin-bottom: 3mm !important;
          }
          .item-attachments-title {
            font-size: 12pt !important;
            color: #000 !important;
          }
          .item-attachments-subtitle {
            font-size: 9pt !important;
            color: #333 !important;
          }
          .smart-image-grid {
            gap: 2mm !important;
          }
          .smart-image-grid.single-image .grid-image {
            max-height: 210mm !important;
            max-width: 185mm !important;
            object-fit: contain !important;
          }
          .smart-image-grid.two-images .grid-image {
            max-height: 120mm !important;
            object-fit: contain !important;
          }
          .smart-image-grid.few-images .grid-image {
            max-height: 100mm !important;
            object-fit: contain !important;
          }
          .smart-image-grid.many-images .grid-image {
            max-height: 70mm !important;
            object-fit: contain !important;
          }
          .grid-image-container {
            border: 1px solid #ccc !important;
          }
          .grid-image {
            width: 100% !important;
            height: auto !important;
            max-width: 100% !important;
            object-fit: contain !important;
          }
          .image-label {
            background: #333 !important;
            padding: 2mm 3mm !important;
            font-size: 8pt !important;
          }
          .other-files-list {
            padding: 2mm !important;
            background: #f5f5f5 !important;
            border: 1px solid #ccc !important;
            margin-top: 2mm !important;
          }
          .other-file-item {
            padding: 1mm 2mm !important;
            font-size: 8pt !important;
          }
          .other-file-item a {
            color: #000 !important;
            text-decoration: underline !important;
          }
          /* Ensure status badges are visible in print */
          .status-badge {
            background: #f0f0f0 !important;
            color: #000 !important;
            border: 1px solid #ccc !important;
          }
          .emergency-badge {
            background: #f0f0f0 !important;
            color: #000 !important;
            border: 1px solid #ccc !important;
          }
          /* Fix for headers and footers */
          .header::before {
            display: none !important;
          }
        }
    </style>
    <script>
      // Smart Image Grid: Aspect ratio detection for optimal layout
      document.addEventListener('DOMContentLoaded', function() {
        var images = document.querySelectorAll('.smart-image-grid .grid-image');
        images.forEach(function(img) {
          function applyAspectClass() {
            if (img.naturalWidth && img.naturalHeight) {
              var ratio = img.naturalWidth / img.naturalHeight;
              var container = img.closest('.grid-image-container');
              if (container) {
                container.classList.remove('aspect-landscape', 'aspect-portrait', 'aspect-square');
                if (ratio > 1.3) {
                  container.classList.add('aspect-landscape');
                } else if (ratio < 0.77) {
                  container.classList.add('aspect-portrait');
                } else {
                  container.classList.add('aspect-square');
                }
              }
            }
          }
          if (img.complete && img.naturalWidth) {
            applyAspectClass();
          } else {
            img.onload = applyAspectClass;
          }
        });
      });
    </script>
</head>
<body>
    <div class="container">
        <div class="header no-break">
            <div class="header-content">
                <h1>${escapedProjectName}</h1>
                <p>${escapedDescription}</p>
                <div class="meta">
                    <p><strong>รายงาน ณ วันที่:</strong> ${reportCreationDate}</p>
                </div>
            </div>
        </div>
        
        <div class="content no-break">
            <div class="summary-grid">
                <div class="summary-card">
                    <h3><span class="icon">📊</span>${t('project.summary', { defaultValue: 'สรุปโครงการ' })}</h3>
                    <div class="stat-item">
                        <span class="stat-label">${t('summary.itemsPurchasedReport', { defaultValue: 'รายการที่ซื้อแล้ว' })}</span>
                        <span class="stat-value">${totalDisplayedItemsInProjectSummary} ${t('summary.itemsUnit', { defaultValue: 'รายการ' })}</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-label">${t('project.teams.involved', { defaultValue: 'ทีมที่เกี่ยวข้อง' })}</span>
                        <span class="stat-value">${teamsInvolved.join(', ')}</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-label">${t('project.deadline', { defaultValue: 'กำหนดเวลา' })}</span>
                        <span class="stat-value">${escapedDeadline}</span>
                    </div>
                </div>
                
                <div class="summary-card">
                    <h3><span class="icon">💰</span>${t('summary.budgetSummary', { defaultValue: 'สรุปงบประมาณ' })}</h3>
                    <div class="stat-item">
                        <span class="stat-label">${t('summary.totalFunds', { defaultValue: 'เงินทุนรวม' })}</span>
                        <span class="stat-value positive">฿${totalReceivedForProject.toLocaleString()}</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-label">${t('stats.total.spent', { defaultValue: 'ใช้จ่ายทั้งหมด' })}</span>
                        <span class="stat-value ${totalSpentOnProject > 0 ? 'negative' : ''}">฿${totalSpentOnProject.toLocaleString()}</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-label">${t('summary.allocatedToPurchasers', { defaultValue: 'เงินที่จัดสรรแล้ว (ให้ผู้ซื้อ)' })}</span>
                        <span class="stat-value">฿${totalAllocatedToPurchasers.toLocaleString()}</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-label">${t('stats.budget.remaining', { defaultValue: 'งบประมาณคงเหลือ' })}</span>
                        <span class="stat-value ${projectBudgetRemaining >= 0 ? 'positive' : 'negative'}">฿${projectBudgetRemaining.toLocaleString()}</span>
                    </div>
                    ${(totalAdditionalFundsGiven && totalAdditionalFundsGiven > 0) ? `
                    <div class="stat-item">
                        <span class="stat-label">${t('summary.additionalFundsForPurchasers', { defaultValue: 'เงินเพิ่มเติม (ให้ผู้ซื้อ)' })}</span>
                        <span class="stat-value">฿${totalAdditionalFundsGiven.toLocaleString()}</span>
                    </div>` : ''}
                    ${(totalFundsReturned && totalFundsReturned > 0) ? `
                    <div class="stat-item">
                        <span class="stat-label">${t('summary.returnedFundsFromPurchasers', { defaultValue: 'เงินที่คืน (จากผู้ซื้อ)' })}</span>
                        <span class="stat-value">฿${totalFundsReturned.toLocaleString()}</span>
                    </div>` : ''}
                </div>
            </div>

            <div class="table-with-title">
                <h2 class="section-title">
                    <span class="icon">🛒</span>${t('summary.itemsByPurchaser', { defaultValue: 'รายการซื้อตามผู้ซื้อ' })}
                </h2>
                ${purchaserSections}
            </div>

            <!-- Fund Entry Attachments Section -->
            <div class="table-with-title">
                <h2 class="section-title">
                    <span class="icon">💰</span>${t('fund.attachments.section', { defaultValue: 'เอกสารการเงิน' })}
                </h2>
                ${fundAttachmentsHtml || `<p style="text-align: center; color: #64748b; padding: 20px;">${t('fund.attachments.none', { defaultValue: 'ไม่มีเอกสารการเงิน' })}</p>`}
            </div>

            <!-- Item Attachments Section (Separate from tables) -->
            ${separateAttachmentsHtml ? `
            <div class="page-break table-with-title">
                <h2 class="section-title">
                    <span class="icon">📎</span>${t('item.attachments.section', { defaultValue: 'ไฟล์แนบรายการ' })}
                </h2>
                ${separateAttachmentsHtml}
            </div>
            ` : ''}
        </div>
    </div>
</body>
</html>`;
};
